import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CursorPage, TrackedKeyword } from '@seo/types';
import { apiPost } from '@/lib/api';

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 90_000;

export type RankCheckResult = {
  project_id: string;
  workflow_id: string;
  outcome: 'done' | 'timed_out';
};

export type StartRankCheckInput =
  | string
  | {
      projectId: string;
      /** When set, only this tracked keyword is checked (auto-trigger on new keyword). */
      trackedKeywordId?: string;
    };

/**
 * Starts a rank-check workflow and polls until every tracked keyword has a
 * fresh `last_checked_at` (or we time out). Exposes a stable checking state
 * so the Tracking UI does not flicker.
 */
export function useStartRankCheck() {
  const qc = useQueryClient();
  const [checkingProjectId, setCheckingProjectId] = useState<string | null>(null);
  const [checkingTrackedKeywordId, setCheckingTrackedKeywordId] = useState<
    string | null
  >(null);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const pollGen = useRef(0);
  const startedAtRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      pollGen.current += 1;
    };
  }, []);

  const clearChecking = (): void => {
    setCheckingProjectId(null);
    setCheckingTrackedKeywordId(null);
    setStartedAt(null);
    startedAtRef.current = null;
  };

  const mutation = useMutation({
    mutationFn: async (input: StartRankCheckInput): Promise<RankCheckResult> => {
      const projectId = typeof input === 'string' ? input : input.projectId;
      const trackedKeywordId =
        typeof input === 'string' ? undefined : input.trackedKeywordId;
      const started = new Date().toISOString();
      startedAtRef.current = started;
      setStartedAt(started);
      setCheckingProjectId(projectId);
      setCheckingTrackedKeywordId(trackedKeywordId ?? null);
      const gen = ++pollGen.current;

      const startedRes = await apiPost<{ workflow_id: string; project_id: string }>(
        '/v1/rank-checks',
        {
          project_id: projectId,
          ...(trackedKeywordId ? { tracked_keyword_id: trackedKeywordId } : {}),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );

      const baseline = startedAtRef.current ?? started;
      const deadline = Date.now() + POLL_TIMEOUT_MS;

      while (Date.now() < deadline) {
        if (pollGen.current !== gen) {
          return {
            project_id: projectId,
            workflow_id: startedRes.workflow_id,
            outcome: 'timed_out',
          };
        }

        await Promise.all([
          qc.refetchQueries({ queryKey: ['tracked-keywords', projectId] }),
          qc.refetchQueries({ queryKey: ['rank-summary', projectId] }),
        ]);

        if (pollGen.current !== gen) {
          return {
            project_id: projectId,
            workflow_id: startedRes.workflow_id,
            outcome: 'timed_out',
          };
        }

        const page = qc.getQueryData<CursorPage<TrackedKeyword>>([
          'tracked-keywords',
          projectId,
        ]);
        const items = page?.items ?? [];
        // When we're only refreshing a single keyword, we only need THAT row
        // to have a fresh timestamp — polling the rest is wasted work and
        // makes the "Checking positions" banner linger for the full timeout.
        const done = trackedKeywordId
          ? items.some(
              (row) => row.id === trackedKeywordId && isCheckedSince(row, baseline),
            )
          : items.length > 0 && items.every((row) => isCheckedSince(row, baseline));
        if (done) {
          return {
            project_id: projectId,
            workflow_id: startedRes.workflow_id,
            outcome: 'done',
          };
        }

        await sleep(POLL_INTERVAL_MS);
      }

      return {
        project_id: projectId,
        workflow_id: startedRes.workflow_id,
        outcome: 'timed_out',
      };
    },
    onError: () => {
      clearChecking();
    },
    onSettled: () => {
      clearChecking();
    },
  });

  const inFlightProjectId =
    checkingProjectId ??
    (mutation.isPending
      ? typeof mutation.variables === 'string'
        ? mutation.variables
        : (mutation.variables?.projectId ?? null)
      : null);

  return {
    ...mutation,
    /** True while a check was started and we are still waiting for results. */
    isChecking: checkingProjectId != null || mutation.isPending,
    checkingProjectId: inFlightProjectId,
    /** When the in-flight check is scoped to a single keyword, its id. */
    checkingTrackedKeywordId,
    /** ISO timestamp when the current check was queued — compare to last_checked_at. */
    checkStartedAt: startedAt,
  };
}

export function isCheckedSince(row: TrackedKeyword, baselineIso: string): boolean {
  if (!row.last_checked_at) return false;
  // Allow small clock skew between browser and API/DB.
  return new Date(row.last_checked_at).getTime() >= new Date(baselineIso).getTime() - 5_000;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
